import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { Modal } from 'antd';
import styled from 'styled-components';
import { scheduleTimeOffBatchRef } from 'ScheduleTimeOffBatch';
import { buildScopeVars, TIME_OFF_ACCENT } from './theme';
import { useTimeOffList } from './hooks/use-time-off-list';
import { timeOffRefreshSignal } from './services/refresh-signal';
import StaffFilterStrip from './components/StaffFilterStrip';
import MonthCalendarFilter from './components/MonthCalendarFilter';
import TimeOffTable from './components/TimeOffTable';
import { IconPlus, IconX } from './components/icons';

export interface TimeOffListModalRef {
  open: () => void;
  close: () => void;
}

export const timeOffListModalRef = React.createRef<TimeOffListModalRef>();

const TimeOffListModal = forwardRef<TimeOffListModalRef>((_props, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  const list = useTimeOffList();
  const { fetchRange, goToday, rangeStart, rangeEnd } = list;

  useImperativeHandle(ref, () => ({
    open: () => { goToday(); setIsOpen(true); },
    close: () => setIsOpen(false),
  }));

  // Refetch when something outside mutates time off (e.g. batch create).
  const refreshTick = timeOffRefreshSignal.value;

  // The selected range IS the query range: fetch on open and on every change.
  useEffect(() => {
    if (isOpen) fetchRange(rangeStart, rangeEnd);
  }, [isOpen, rangeStart, rangeEnd, fetchRange, refreshTick]);

  const openCreate = () => scheduleTimeOffBatchRef.current?.open();

  return (
    <ModalStyled
      open={isOpen}
      onCancel={() => setIsOpen(false)}
      closable={false}
      footer={null}
      centered
      width={1320}
      destroyOnClose={false}
      styles={{
        content: { padding: 0, borderRadius: 18, overflow: 'hidden' },
        body: { padding: 0 },
        mask: { background: 'rgba(0,24,72,0.45)' },
      }}
    >
      <Root>
        <Header>
          <div className="left">
            <NewBtn onClick={openCreate}>
              <IconPlus size={16} strokeWidth={2.4} />
              New Time Off
            </NewBtn>
          </div>
          <span className="title">Time Off List</span>
          <div className="right">
            <CloseBtn onClick={() => setIsOpen(false)} title="Close" aria-label="Close">
              <IconX size={20} strokeWidth={2.6} />
            </CloseBtn>
          </div>
        </Header>

        <StaffFilterStrip
          staffs={list.staffs}
          selectedStaffIds={list.selectedStaffIds}
          perStaffCounts={list.perStaffCounts}
          onToggle={list.toggleStaff}
          onClear={list.clearStaff}
        />

        <Body>
          <MonthCalendarFilter
            viewYear={list.viewYear}
            viewMonth={list.viewMonth}
            rangeStart={list.rangeStart}
            rangeEnd={list.rangeEnd}
            pickPhase={list.pickPhase}
            onPrevMonth={list.prevMonth}
            onNextMonth={list.nextMonth}
            onPickDay={list.pickDay}
          />
          <TimeOffTable
            entries={list.filteredEntries}
            staffs={list.staffs}
            indexById={list.indexById}
            avatarById={list.avatarById}
            selectedStaffIds={list.selectedStaffIds}
            rangeStart={list.rangeStart}
            rangeEnd={list.rangeEnd}
            loading={list.loading}
            pendingDeleteId={list.pendingDeleteId}
            onRequestDelete={id => list.setPendingDeleteId(id)}
            onCancelDelete={() => list.setPendingDeleteId(null)}
            onConfirmDelete={id => list.deleteEntry(id)}
          />
        </Body>
      </Root>
    </ModalStyled>
  );
});

TimeOffListModal.displayName = 'TimeOffListModal';

export default TimeOffListModal;

const ModalStyled = styled(Modal)`
  .ant-modal-content { padding: 0; }
`;

const Root = styled.div`
  ${buildScopeVars(TIME_OFF_ACCENT)}
  display: flex;
  flex-direction: column;
  height: min(880px, 88vh);
  max-height: 88vh;
  overflow: hidden;
  background: var(--bg-surface);
`;

// Header adopts the app's standard modal-header style (see QuickBooking/Modal):
// light blue band, blue bottom rule, centered dark title, orange close action.
const Header = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 56px;
  padding: 0 16px;
  background: #E8F3FF;
  border-bottom: 1px solid #148CF1;
  flex-shrink: 0;
  .left { display: flex; align-items: center; }
  .right { display: flex; align-items: center; gap: 8px; }
  .title {
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    color: #1D2129;
    text-align: center;
    font-family: Poppins, inherit;
    font-size: 20px;
    font-weight: 600;
    line-height: 24px;
    text-transform: uppercase;
    white-space: nowrap;
  }
`;

const NewBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 9px 16px;
  border-radius: 5px;
  border: none;
  background: var(--acc);
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.03em;
  font-family: inherit;
  cursor: pointer;
  &:hover { background: var(--acc-hover); }
`;

const CloseBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 34px;
  border: none;
  border-radius: 5px;
  background: #fff;
  color: #F05326;
  cursor: pointer;
  &:hover { background: #FFEFEB; }
`;

const Body = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
`;
